import './HeaderNav.scss';

import classNames from 'classnames';
import { Fragment, useMemo } from 'react';
import { Container, Nav, Navbar } from 'react-bootstrap';
import { Link, useLocation, useSearchParams } from 'react-router';

import { BRIDGE_BIO_LOGO_SRC, shouldHideUtilityResources } from '@constants/bridgeBio';
import { MYOME_URL } from '@constants/constants';
import { ACCOUNT, FVT_FAMILY, FVT_INVITE, FVT_LOGIN, FVT_WELCOME, HOMEPAGE, RESULTS } from '@constants/routes';
import useApp from '@hooks/useApp';
import { useOpenReplayDebugToggle } from '@hooks/useOpenReplayDebugToggle';
import useViewportSizing from '@hooks/useViewportSizing';
import { getPartnerConfigByCampaignCode } from '@pages/order/pages/Launch/partnerConfig';
import FullLogoSvg from '@statics/images/full_logo.svg?react';
import { recordGoogleAnalyticsEvent } from '@utils/helpers';

import { getHeaderUtilityNavItems } from './Header.consts';
import { IHeaderNavItem, isHeaderNavItemActive } from './Header.interface';
import UtilityNav from './UtilityNav';

const PERSON_NAV_ROUTES = new Set<string>([HOMEPAGE, RESULTS]);

const routeWithSelectedIndividual = (route: string, individualId: string | null) => {
  if (!individualId || !PERSON_NAV_ROUTES.has(route)) return route;
  return `${route}?individual=${encodeURIComponent(individualId)}`;
};

type HeaderNavProps = {
  partnerLogoSrc?: string;
  external_member_id?: string;
  noLoginButton?: boolean;
};

const HeaderNav = ({
  partnerLogoSrc: propsPartnerLogoSrc,
  external_member_id: propsExternalMemberId,
  noLoginButton,
}: HeaderNavProps) => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  // Counseling keeps `viewing` for the same person Home and Results call `individual`.
  const selectedIndividualId = searchParams.get('individual') || searchParams.get('viewing');
  const {
    campaignCode,
    campaignLogoUrl,
    hasFVTReferral,
    isFvtOnlyProband,
    isLoadingUserInfo,
    isLoggedIn,
    onLogin,
    onLogout,
    external_member_id: appExternalMemberId,
  } = useApp();

  const { isMobile } = useViewportSizing();
  const { isEnabled: isDebugToggleEnabled, toggle: toggleDebug, isDebugSupported } = useOpenReplayDebugToggle();

  const external_member_id = propsExternalMemberId || appExternalMemberId;

  const isFvtRoute = pathname.startsWith('/fvt');
  const isPublicFvtRoute = pathname === FVT_WELCOME || pathname === FVT_LOGIN;
  const hidePortalLoginButton = noLoginButton || isPublicFvtRoute;
  const showResources = !shouldHideUtilityResources(pathname, campaignCode, isLoadingUserInfo);
  const utilityNavItems = useMemo(() => getHeaderUtilityNavItems(showResources), [showResources]);

  const partnerLogoSrc = useMemo(() => {
    if (propsPartnerLogoSrc) return propsPartnerLogoSrc;

    if (!isLoggedIn && isFvtRoute) return BRIDGE_BIO_LOGO_SRC;

    if (campaignLogoUrl) return campaignLogoUrl;

    const config = getPartnerConfigByCampaignCode(campaignCode);
    if (config?.logoFileName) return `/partners/${config.logoFileName}`;

    return undefined;
  }, [propsPartnerLogoSrc, isLoggedIn, isFvtRoute, campaignLogoUrl, campaignCode]);

  const navItems: IHeaderNavItem[] = useMemo(() => {
    if (!isLoggedIn) {
      const items: IHeaderNavItem[] = [];

      if (isMobile) {
        items.push(...utilityNavItems);
      }

      if (!hidePortalLoginButton) {
        items.push({
          label: 'Login',
          onClick: onLogin,
        });
      }

      return items;
    }

    const items: IHeaderNavItem[] = [];

    // Profile + orders are unknown until this settles; don't flash the default portal links.
    if (!isLoadingUserInfo) {
      if (!isFvtOnlyProband) {
        // My Orders leaves primary nav; Home subsumes it and links through to it.
        items.push(
          {
            label: 'Home',
            route: HOMEPAGE,
          },
          {
            label: 'My Results',
            route: RESULTS,
          },
        );
      }

      if (hasFVTReferral) {
        items.push({
          label: 'Family Testing',
          route: FVT_FAMILY,
          activeRoutes: [FVT_FAMILY, FVT_INVITE],
        });
      }

      items.push({
        label: 'My Account',
        route: ACCOUNT,
        newLine: isMobile,
      });
    }

    if (isMobile) {
      items.push(...utilityNavItems);

      if (isDebugSupported && toggleDebug) {
        items.push({
          label: `${isDebugToggleEnabled ? '☑' : '☐'} Show OpenReplay Sanitization`,
          onClick: toggleDebug,
          newLine: true,
          className: 'openreplay-toggle-link',
        });
      }

      items.push({
        label: 'Logout',
        onClick: onLogout,
      });
    }

    return items;
  }, [
    hasFVTReferral,
    isFvtOnlyProband,
    isLoadingUserInfo,
    isLoggedIn,
    isMobile,
    onLogin,
    onLogout,
    hidePortalLoginButton,
    utilityNavItems,
    isDebugSupported,
    isDebugToggleEnabled,
    toggleDebug,
  ]);

  const onNavClick = (item: IHeaderNavItem) => {
    // Analytics tracking for navigation
    if (item.route) {
      recordGoogleAnalyticsEvent('User Interaction', 'Button Click', `Nav Link Route: ${item.route}`);
    }
    // onClick handlers for non-route actions (like logout)
    item.onClick?.();
  };

  return (
    <header className={classNames('bg-white position-sticky', partnerLogoSrc && 'header--co-brand')}>
      {!isMobile && <UtilityNav showResources={showResources} />}

      <Navbar collapseOnSelect expand='md' className='nav-items-bar'>
        <Container fluid='xxl' className='py-2'>
          <Navbar.Brand>
            <div className='brand-wrap d-flex align-items-center '>
              <div className='brand-logo-wrap'>
                {pathname.startsWith('/access') ? (
                  <a href={MYOME_URL} style={{ display: 'contents' }}>
                    <FullLogoSvg className='navbar-logo' desc='logo' title='logo' />
                  </a>
                ) : isPublicFvtRoute ? (
                  <Link to={FVT_WELCOME} style={{ display: 'contents' }}>
                    <FullLogoSvg className='navbar-logo' desc='logo' title='logo' />
                  </Link>
                ) : (
                  <Link
                    to={
                      isFvtOnlyProband || (isLoadingUserInfo && isFvtRoute)
                        ? FVT_FAMILY
                        : routeWithSelectedIndividual(HOMEPAGE, selectedIndividualId)
                    }
                    style={{ display: 'contents' }}
                  >
                    <FullLogoSvg className='navbar-logo' desc='logo' title='logo' />
                  </Link>
                )}
              </div>
              {partnerLogoSrc ? (
                <>
                  <span className='divider mt-2' aria-hidden='true' />
                  <div className='co-brand d-flex flex-column justify-content-center'>
                    <img className='partner-logo' src={partnerLogoSrc} alt='Partner logo' />
                    {external_member_id && (
                      <span className='member-id text-secondary small mt-2' data-openreplay-sanitize>
                        ID: <b>{external_member_id}</b>
                      </span>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls='navItems' />

          <Navbar.Collapse id='navItems'>
            <Nav className='ms-auto'>
              {navItems.map(item => (
                <Fragment key={item.label}>
                  {item.route ? (
                    <Nav.Link
                      as={Link}
                      to={routeWithSelectedIndividual(item.route, selectedIndividualId)}
                      active={isHeaderNavItemActive(pathname, item)}
                      onClick={() => onNavClick(item)}
                    >
                      {item.label}
                    </Nav.Link>
                  ) : item.url ? (
                    <Nav.Link
                      href={item.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={() => onNavClick(item)}
                    >
                      {item.label}
                    </Nav.Link>
                  ) : (
                    <Nav.Link onClick={() => onNavClick(item)} className={item.className}>
                      {item.label}
                    </Nav.Link>
                  )}
                  {item.newLine && <div className='hr my-2' />}
                </Fragment>
              ))}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </header>
  );
};

export default HeaderNav;
